import state from './state';
import getters from '@/store/getters';
import EntityDetailsHandler from '@/plugins/entity-details-handler';
import { PremiumChangeWithIdAsNumber } from '@/types/premiums';
import { PostData, Entity, EntityWithIdAsString } from '@/types/postData';
import {
	EnrichedCampaignGeotargetingDto, EnrichedOrganisationGeotargetingDto, EnrichedStudyGeotargetingDto
} from '@studyportals/campaign-management-api-interface';

import { IMutations } from '@/types/store/i-mutations';

const mutations: IMutations = {
	setCampaignData: (data) => {
		if (!data.studies) data.studies = [];
		if (!data.organisations) data.organisations = [];

		const convertItem = (item: EntityWithIdAsString | EnrichedOrganisationGeotargetingDto | EnrichedStudyGeotargetingDto): Entity => {
			const itemWithIdAsNumber: Entity = {
				id: typeof item.id === 'string' ? parseInt(item.id, 10) : item.id,
				targeted_countries: item.targeted_countries
			};
			if (item.name) {
				itemWithIdAsNumber.name = item.name;
			}
			return itemWithIdAsNumber;
		};

		const campaignData: PostData = {
			campaign_id: data.campaign_id,
			expiry_date: data.expiry_date,
			update_date: data.update_date,
			studies: data.studies.map(convertItem),
			organisations: data.organisations.map(convertItem)
		};

		mutations.updateCampaignData(campaignData);
	},
	updateCampaignData: (data) => {
		state.postData = data as EnrichedCampaignGeotargetingDto;
		state.originalData = JSON.parse(JSON.stringify(data));
	},
	setDetailsOfAllEntities: (data) => {
		state.detailsOfAllEntities = data;
	},
	setCountryChecked: (data) => {
		const id = data.id.toString();
		state.countries[id].checked = data.checked;
	},
	setSelectedContinent: (continentId) => {
		const conId = continentId.toString();

		Object.keys(state.continents).forEach((id) => {
			state.continents[id].selected = id === conId;
		});
	},
	setCountryPremiumForEntity: (entities, data) => {
		entities.forEach((entity) => {
			const levels = Object.keys(entity.targeted_countries);
			const targetedCountries = entity.targeted_countries as unknown as Record<string, number[]>;

			levels.forEach((level) => {
				const countryList = targetedCountries[level];
				const countrySet = new Set(countryList);
				countrySet.delete(data.id);
				targetedCountries[level] = [...countrySet];
			});
		});

		if (data.level === 'b') {
			return;
		}

		entities.forEach((_, index) => {
			const targetedCountries = entities[index].targeted_countries as unknown as Record<string, number[]>;
			if (!targetedCountries[data.level]) {
				targetedCountries[data.level] = [];
			}

			targetedCountries[data.level].push(data.id);
		});
	},
	setCountryPremium: (data) => {
		const dataWithIdAsNumber: PremiumChangeWithIdAsNumber = {
			id: parseInt(data.id, 10),
			level: data.level
		};
		mutations.setCountryPremiumForEntity(getters.configurationOfSelectedEntities(), dataWithIdAsNumber);
	},
	resetCountryPremiumsForSelectedEntities: () => {
		state.detailsOfSelectedEntities.forEach((selectedEntity) => {
			const entity = EntityDetailsHandler.retrieveSelectedEntity(state.postData, selectedEntity);
			if (!entity) {
				return;
			}

			const premiumStates = Object.keys(state.premiumStates);
			premiumStates.forEach((premiumState) => {
				if (premiumState === 'b') {
					return;
				}

				(entity.targeted_countries as unknown as Record<string, number[]>)[premiumState] = [];
			});
		});
	},
	markEntityAsSelected: (entity) => {
		state.detailsOfSelectedEntities.push(entity);
	},
	markEntityAsNotSelected: (entity) => {
		const index = state.detailsOfSelectedEntities.findIndex(
			(selectedEntity) => selectedEntity.id === entity.id && selectedEntity.type === entity.type
		);
		if (index === -1) {
			return;
		}

		state.detailsOfSelectedEntities.splice(index, 1);
		mutations.resetPostData();
	},
	markAllEntitiesAsNotSelected: () => {
		state.detailsOfSelectedEntities = [];
		mutations.resetPostData();
	},
	setPricingForSelectedEntities: (pricing) => {
		state.detailsOfSelectedEntities.forEach((selectedEntity) => {
			const entity = EntityDetailsHandler.retrieveSelectedEntity(state.postData, selectedEntity);
			if (!entity) {
				return;
			}

			entity.pricing = pricing;
		});

		// Ensure that the changes in postData will be acknowledged by Vuex and will result in Vuex getters being updated.
		state.postData = JSON.parse(JSON.stringify(state.postData));
	},
	setSelectedPresetIndex: (index) => {
		state.selectedPresetIndex = index;
	},
	resetPostData: () => {
		// Reset postData in case original data was overwritten with all countries as basic for selected entities with different targeting.
		state.postData = JSON.parse(JSON.stringify(state.originalData));
	},
	setPricingProducts: (products) => {
		state.pricingProducts = products;
	},
	setCampaignEntityConfigItems: (campaignEntityConfigItems) => {
		state.campaignEntityConfigItems = campaignEntityConfigItems;
	},
	setInitialEntityPricingSnapshot: (snapshot: Record<string, string | undefined>) => {
		state.initialEntityPricingSnapshot = snapshot;
	},
	updateSearchQueryCountries: (query) => {
		state.searchQueryCountries = query;

		const continentId = getters.selectedContinentId();
		const relevantContinents = getters.relevantContinentsGivenSearchQuery();
		if (continentId && relevantContinents[continentId] !== undefined) {
			return;
		}

		const relevantContinentIds = Object.keys(relevantContinents);
		if (relevantContinentIds.length === 0) {
			return;
		}

		mutations.setSelectedContinent(parseInt(relevantContinentIds[0]));
	},
	setStudyId: (id) => {
		state.studyId = id;
	},
	setOrganisationId: (id) => {
		state.organisationId = id;
	},
	setCampaignId: (id) => {
		state.campaignId = id;
	},
	setCampaignStartDate: (timeStamp) => {
		state.campaignStartDate = timeStamp;
	},
	setLoadingState: (newState) => {
		state.loading = {
			...state.loading,
			state: newState
		};
	},
	setLoadingTime: (time) => {
		state.loading.estimatedTimeNeededForSaving = time;
	},
	setCurrentRoute: (name, props = {}) => {
		state.router = {
			...state.router,
			current: name,
			props
		}
	},
	setIsListingSession: (isListingSession) => {
		state.isListingSession = isListingSession;
	},
	markAsInitialised: () => {
		state.initialised = true;
	},
	setStandAloneLogin: (standAloneLogin) => {
		state.standAloneLogin = standAloneLogin;
	},
	setEnrichedStudyEntityMap: (entities: EnrichedStudyGeotargetingDto[]) => {
		state.enrichedStudyEntityMap = new Map(
			entities.map((entity) => {
				const relevantPricing = state.postData.studies.find(
					(study) => study.id === entity.id
				)?.pricing;
				return [entity.id, { ...entity, pricing: relevantPricing }];
			})
		);
	},
	setEnrichedOrganisationEntityMap: (entities: EnrichedOrganisationGeotargetingDto[]) => {
		state.enrichedOrganisationEntityMap = new Map(
			entities.map((entity) => {
				const relevantPricing = state.postData.organisations.find(
					(organisation) => organisation.id === entity.id
				)?.pricing;
				return [entity.id, { ...entity, pricing: relevantPricing }];
			})
		);
	},
	setModalState: (modalState) => {
		state.modalState = modalState;
	}
};

export default mutations;
